import { useState } from 'react'

import { useEditTag, useTags } from '../api'
import { AssetTag } from '../components/TableHeaderTags/TableHeaderTags.types'
import { useTagsStore } from '../store'

interface UseTagEditOptions {
  onSuccess?: (updatedTag: AssetTag) => void
  onError?: (error: unknown) => void
}

/**
 * Hook to handle tag editing functionality with API integration
 * @param options Configuration options
 * @returns Object containing tag editing state and functions
 */
export const useTagEdit = (options: UseTagEditOptions = {}) => {
  const { onSuccess, onError } = options
  const [isEditing, setIsEditing] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const { trigger: editTag, isMutating } = useEditTag()
  const { globalOrgTags } = useTagsStore()
  const { mutate: refetchTags } = useTags({ page: 1, pageSize: 100 })

  const updateTag = async (tagId: number, name?: string, color?: string) => {
    const trimmedName = name?.trim()
    if (!trimmedName || !color) return null

    // Reset validation error
    setValidationError(null)

    // Check if tag name already exists in available tags (excluding the current tag)
    if (trimmedName) {
      // Check minimum length requirement
      if (trimmedName.length < 3) {
        setValidationError('Tag name must be at least 3 characters')
        return
      }

      const tagExists = globalOrgTags.some(
        (tag) => tag.name.toLowerCase() === trimmedName.toLowerCase() && tag.id !== tagId,
      )

      if (tagExists) {
        const error = `A tag with the name "${trimmedName}" already exists`
        setValidationError(error)
        return
      }
    }

    setIsEditing(true)
    try {
      return editTag(
        {
          params: { id: tagId },
          ...(trimmedName && { name: trimmedName }),
          ...(color && { color }),
        },
        {
          onSuccess: async (updatedTag) => {
            // Refetch tags from backend to get the updated list
            await refetchTags()

            // Immediately update globalOrgTags so the popout reflects the change
            useTagsStore.getState().updateTagInGlobalState(tagId, updatedTag)

            //update the tags in the filteringTags in tagsstore
            const { filteringTags } = useTagsStore.getState()

            const updatedFilteringTags = filteringTags.map((tag) =>
              tag.id === tagId ? { ...tag, name: updatedTag.name, color: updatedTag.color } : tag,
            )
            useTagsStore.getState().setFilteringTags(updatedFilteringTags)

            if (onSuccess) {
              onSuccess(updatedTag)
            }

            setIsEditing(false)
          },
          onError: (error) => {
            console.error('Failed to update tag:', error)
            if (onError) {
              onError(error)
            }
            setIsEditing(false)
          },
        },
      )
    } catch (error) {
      console.error('Failed to update tag:', error)
      if (onError) {
        onError(error)
      }
      setIsEditing(false)
      return null
    }
  }

  return {
    updateTag,
    isEditing: isEditing || isMutating,
    validationError,
  }
}
